"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Star } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselAutoPlay,
  Typography,
  type CarouselApi,
} from "@drpcrd/shared/ui";
import { cn } from "@drpcrd/utils";
import { CreatorInfo } from "../creator-info";
import { BakedCreatorInfo } from "../baked-with-nutcake/baked-creator-info";
import { CarouselNavButtons } from "./carousel-nav-buttons";

export type CreatorVideoItem = {
  image: string;
  name: string;
  handle: string;
  avatar: string | null;
  localAvatar?: string;
  socialAvatar?: string | null;
  badge?: string | null;
  video?: string | null;
  href?: string | null;
};

type CardProps = {
  item: CreatorVideoItem;
  showDetails: boolean;
};

const CreatorVideoCard = ({ item, showDetails }: CardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isHoveredRef = useRef(false);

  const attemptPlay = () => {
    if (!videoRef.current) return;
    void videoRef.current.play().catch((error: unknown) => {
      if (error instanceof DOMException) {
        if (
          error.name === "NotSupportedError" ||
          error.name === "NotAllowedError"
        ) {
          setHasVideoError(true);
          setIsVideoReady(false);
        }
      }
    });
  };

  const onMouseEnter = () => {
    isHoveredRef.current = true;
    setIsHovered(true);
    if (!item.video || hasVideoError) return;
    attemptPlay();
  };

  const onMouseLeave = () => {
    isHoveredRef.current = false;
    setIsHovered(false);
    setIsVideoReady(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const cardClassName =
    "space-y-2 rounded-2xl p-0.75 pb-0 md:pb-0.75 transition-colors hover:bg-neutral-100";

  const cardContent = (
    <>
      <div className="aspect-vertical relative overflow-hidden rounded-xl">
        <Image
          src={item.image}
          alt={item.name}
          fill
          sizes="(max-width: 640px) 50vw, 25vw"
          className={cn(
            "object-cover transition-opacity duration-300",
            isHovered && item.video && isVideoReady
              ? "opacity-0"
              : "opacity-100",
          )}
        />
        {item.video && !hasVideoError && (
          <video
            ref={videoRef}
            src={item.video}
            muted
            loop
            playsInline
            preload="metadata"
            onCanPlay={() => {
              if (isHoveredRef.current && videoRef.current?.paused) {
                attemptPlay();
              }
            }}
            onPlaying={() => setIsVideoReady(true)}
            onError={() => {
              setHasVideoError(true);
              setIsVideoReady(false);
            }}
            className={cn(
              "absolute inset-0 h-full w-full object-cover transition-opacity duration-300",
              isHovered && isVideoReady ? "opacity-100" : "opacity-0",
            )}
          />
        )}
        {item.badge && (
          <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 rounded-full bg-pink-500 px-1 py-0.5">
            <Star size={10} className="shrink-0 fill-white text-white" />
            <Typography
              size="xs"
              weight="semibold"
              className="text-white normal-case"
            >
              {item.badge}
            </Typography>
          </div>
        )}
      </div>
      {showDetails && (
        <div>
          {item.localAvatar ? (
            <BakedCreatorInfo
              name={item.name}
              handle={item.handle}
              avatarSrc={item.localAvatar}
            />
          ) : (
            <CreatorInfo
              name={item.name}
              handle={item.handle}
              creatorSrc={item.avatar}
              socialAvatarSrc={item.socialAvatar ?? null}
            />
          )}
        </div>
      )}
    </>
  );

  if (item.href) {
    const isExternal = item.href.startsWith("http");

    return (
      <Link
        href={item.href}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
        className={cn("block", cardClassName)}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {cardContent}
      </Link>
    );
  }

  return (
    <div
      className={cn("cursor-pointer", cardClassName)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {cardContent}
    </div>
  );
};

type Props = {
  items: CreatorVideoItem[];
  /** Show creator name and handle below the image. Defaults to false. */
  showDetails?: boolean;
  autoplayDelay?: number;
};

/** Creator video carousel with autoplay and prev/next navigation. */
export const CreatorVideoCarousel = ({
  items,
  showDetails = false,
  autoplayDelay = 3000,
}: Props) => {
  const autoplayPlugin = useRef(
    CarouselAutoPlay({
      delay: autoplayDelay,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    }),
  );

  const [carouselApi, setCarouselApi] = useState<CarouselApi>();

  return (
    <div className="space-y-3">
      <div className="mr-[calc(50%-50vw)] overflow-x-clip pr-[calc(50vw-50%)] md:mr-0 md:pr-0">
        <Carousel
          className="[&>div]:overflow-visible! md:[&>div]:overflow-hidden!"
          opts={{ loop: true, align: "start" }}
          plugins={[autoplayPlugin.current]}
          setApi={setCarouselApi}
        >
          <CarouselContent className="ml-0">
            {items.map((item, index) => (
              <CarouselItem
                key={`${item.name}-${index}`}
                className="basis-1/2 pl-0 md:basis-1/3 lg:basis-1/4"
              >
                <CreatorVideoCard item={item} showDetails={showDetails} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
      <div className="flex items-center justify-end gap-1">
        <CarouselNavButtons
          onPrev={() => carouselApi?.scrollPrev()}
          onNext={() => carouselApi?.scrollNext()}
        />
      </div>
    </div>
  );
};
